import { Router } from '@angular/router';
import { environment } from './../../../environments/environment';
import { AppActions } from './../store/app.reducer';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IState } from '../store/root.reducer';
import { CurrentUserActions } from '../store/current-user.reducer';
import { EMPTY } from 'rxjs';
import { FullScreenStateActions } from './../store/full-screen.reducer';
import { AppSettingsService } from './app-settings.service';
import { map, share } from 'rxjs/operators';
import * as jwt_decode from 'jwt-decode';
import { CommonApiService } from './commons-api.service';

const tokenEndPoint = 'oauth2/token';
const redirectEndpoint = 'redirect';
const recoverEndpoint = 'redirect/recover';
const externalLogin = 'external-login?guid=';

const refreshTokenKey = 'ops.id.refreshToken';
const accessTokenKey = 'ops.id.accessToken';
const expiresTokenKey = 'ops.id.accessTokenExpiration';
const currentUserKey = 'ops.id.currentUser';

@Injectable()
export class AuthService {
  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private store: Store<IState>,
    private settings: AppSettingsService,
    private router: Router,
    private commonApiService: CommonApiService
  ) {}

  login(cfid: string) {
    this.localStorage.clearAll();
    this.requestAccessToken(cfid).subscribe(() => {
      this.mapTokenToUser(cfid);
      this.settings.reloadSettings();
      this.reloadData();
      this.router.navigate(['claim-status/claims']);
    });
  }

  private mapTokenToUser(cfid: string) {
    try {
      const decodedToken = jwt_decode(this.getAccessToken());
      const currentUser: User = {
        id: +decodedToken.ContactId,
        grantType: decodedToken.GrantType,
        features: decodedToken.Features,
        featuresCodes: decodedToken.role,
        companyId: +decodedToken.CompanyId,
        defaultContact: 0,
        contactId: +decodedToken.ContactId,
        fullName: decodedToken.FullName,
        timeZone: undefined,
        userName: decodedToken.unique_name,
        userType: +decodedToken.UserType,
        emailAddresses: [],
        shippingVendorId: +decodedToken.ShippingVendorId,
        manufacturers: decodedToken,
        showTutorial: undefined,
        shopId: undefined,
        firstName: decodedToken.FirstName,
        lastName: decodedToken.LastName,
        companyName: decodedToken.CompanyName,
        userGuid: undefined,
        marketId: 0,
        cfid: decodedToken.Cfid ? decodedToken.Cfid : cfid,
        oecCompanyId: decodedToken?.OecCompanyId,
        oecUserId: decodedToken?.OecUserId,
        opsUserId: decodedToken.OpsUserId,
        isProxyLogin: decodedToken.isProxyLogin,
      };
      this.localStorage.set(currentUserKey, currentUser);
      this.store.dispatch({
        type: CurrentUserActions.SET_USER,
        payload: currentUser,
      });
    } catch (error) {
      this.logout();
    }
  }

  getAccessToken(): string {
    const token = this.localStorage.get(accessTokenKey);
    if (token) {
      return token.toString();
    }
    return '';
  }

  private mapTokenResponse(res: any) {
    if (res.userName && res.access_token) {
      this.localStorage.set(accessTokenKey, res.access_token);
      this.localStorage.set(expiresTokenKey, res['.expires']);
      this.localStorage.set(refreshTokenKey, res.refresh_token);
      return res.access_token;
    } else {
      return null;
    }
  }

  refreshToken(): Observable<string> {
    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;
    const refreshToken = this.localStorage.get(refreshTokenKey);
    let body: HttpParams;
    if (refreshToken) {
      body = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('client_Id', environment.clientId)
        .set('refresh_token', refreshToken.toString());
    } else {
      window.location.href = environment.logoutRedirectUrl;
      return EMPTY;
    }
    return this.http
      .post<any>(url, body, this.getOptions())
      .pipe(
        map((res) => {
          if (!res.access_token) {
            return null;
          }

          const accessToken = this.mapTokenResponse(res);
          return accessToken;
        })
      )
      .pipe(share());
  }

  private getOptions() {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/x-www-form-urlencoded'
      ),
    };
    return httpOptions;
  }

  requestAccessToken(cfid: string): Observable<string> {
    if (!cfid) {
      throw new Error(`cfid can not be empty`);
    }
    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;
    const body = new HttpParams()
      .set('grant_type', 'coldfusionsessionid')
      .set('client_Id', environment.clientId)
      .set('cfid', cfid);

    return this.http.post<any>(url, body, this.getOptions()).pipe(
      map((res) => {
        if (!res.access_token) {
          return null;
        }
        const accessToken = this.mapTokenResponse(res);
        return accessToken;
      })
    );
  }

  isAuthenticated(): boolean {
    return this.localStorage.get(accessTokenKey) ? true : false;
  }

  getCurrentUser(): User {
    return this.localStorage.get(currentUserKey) as User;
  }

  private reloadData() {
    this.store
      .pipe(select((state: IState) => state.currentUser))
      .subscribe((user) => {
        this.store.dispatch({
          type: AppActions.RELOAD_DATA,
          payload: true,
        });
        if (user.contactId) {
          this.settings.reloadSettings();
        }
      });
  }

  logout() {
    this.clearUserFromLocalStorage();

    this.store.dispatch({
      type: CurrentUserActions.INIT,
    });
    this.store.dispatch({
      type: FullScreenStateActions.INIT,
    });
    this.store.dispatch({
      type: AppActions.INIT,
    });
    this.store.dispatch({
      type: AppActions.RELOAD_DATA,
    });

    this.commonApiService.GetKeycloakEnabled().subscribe((resp) => {
      if (resp.enabled) {
        window.location.href = `${environment.oecLogoutUrl}?redirect_uri=${environment.baseUrl}`;
      } else {
        window.location.href = `${environment.logoutRedirectUrl}`;
      }
    });
  }

  clearUserFromLocalStorage() {
    this.localStorage.clearAll();
  }

  loadUserFromLocalStore() {
    const currentUser = this.localStorage.get(currentUserKey);
    if (currentUser) {
      this.store.dispatch({
        type: CurrentUserActions.SET_USER,
        payload: currentUser,
      });
      this.settings.reloadSettings();
    }
  }

  redirect(
    targetUrl: string,
    parameters: string,
    targetAppUrl: string
  ): Observable<string> {
    const currentUser = this.localStorage.get(currentUserKey);
    const accessToken = this.localStorage.get(accessTokenKey);
    const expiresToken = this.localStorage.get(expiresTokenKey);
    const refreshToken = this.localStorage.get(refreshTokenKey);

    if (!accessToken || !expiresToken || !refreshToken || !currentUser) {
      return EMPTY;
    }

    const requestData: RedirectData = {
      accessToken: accessToken.toString(),
      expiresToken: expiresToken.toString(),
      refreshToken: refreshToken.toString(),
      currentUser: JSON.stringify(currentUser),
      url: targetUrl,
      parameters: parameters,
    };

    const options = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.getAccessToken()}`,
      }),
    };
    const url = `${environment.identityApiBaseUrl}${redirectEndpoint}`;
    return this.http.post(url, requestData, options).pipe(
      map((res) => {
        return `${targetAppUrl}${externalLogin}${res}`;
      })
    );
  }

  relogin(accessToken?: string) {
    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;
    const body: HttpParams = new HttpParams()
      .set('grant_type', 'relogin')
      .set('client_Id', environment.clientId)
      .set('accessToken', accessToken.toString())
      .set('environment', environment.environment);
    return this.http.post(url, body, this.getOptions());
  }

  tokenLogin(accessToken?: string) {
    this.localStorage.clearAll();
    this.relogin(accessToken).subscribe((token) => {
      this.mapTokenResponse(token);
      this.mapTokenToUser(null);
      this.settings.reloadSettings();
      this.reloadData();
      this.router.navigate(['/claim-status/claims']);
    });
  }

  loginExternal(guid): Observable<RedirectDataC> {
    const requestData = { guid: guid };
    const options = { headers: new HttpHeaders({}) };
    const url = `${environment.identityApiBaseUrl}${recoverEndpoint}`;
    return this.http.post(url, requestData, options).pipe(
      map((res) => {
        if (res) {
          const result: RedirectDataC = res as RedirectDataC;
          if (result) {
            this.localStorage.set(accessTokenKey, result.AccessToken);
            this.localStorage.set(expiresTokenKey, result.ExpiresToken);
            this.localStorage.set(refreshTokenKey, result.RefreshToken);
            const currentUser = JSON.parse(result.CurrentUser);
            this.localStorage.set(currentUserKey, currentUser);
            this.store.dispatch({
              type: CurrentUserActions.SET_USER,
              payload: currentUser,
            });
            this.reloadData();
            return result;
          } else {
            window.location.href = `${environment.logoutRedirectUrl}`;
          }
        } else {
          window.location.href = `${environment.logoutRedirectUrl}`;
        }
      })
    );
  }
}

export interface RequestBody {
  grant_type: string;
  client_Id: string;
  cfid: string;
}

export interface User {
  id: number;
  grantType: string;
  features: string[];
  featuresCodes: string[];
  companyId: number;
  defaultContact: number;
  contactId: number;
  fullName: string;
  timeZone: string;
  userName: string;
  userType: number;
  emailAddresses: string[];
  shippingVendorId: number;
  manufacturers: string[];
  showTutorial: number;
  shopId: number;
  firstName: string;
  lastName: string;
  companyName: string;
  userGuid: string;
  marketId: number;
  cfid?: string;
  oecCompanyId?: string;
  oecUserId?: string;
  opsUserId: number;
  pendo?: IUserPendoInfo;
  isValutraxPendoEnabled?: boolean;
  pendoApiKey?: string;
  isProxyLogin: boolean;
}

interface RedirectData {
  accessToken: string;
  expiresToken: string;
  refreshToken: string;
  currentUser: string;
  url: string;
  parameters: string;
}

interface RedirectDataC {
  AccessToken: string;
  ExpiresToken: string;
  RefreshToken: string;
  CurrentUser: string;
  Url: string;
  Parameters: string;
}

export interface IUserPendoInfo {
  userType?: string;
  titleCategory?: string;
  assignedRoles?: string;
  msoGroup?: string;
  msoShopId?: string;
  supplierGroup?: string;
  market?: string;
  supplierRole?: string;
  packageName?: string;
  supplierDmsTypes?: string;
  makeTypes?: string;
  manufacturer?: string;
  estimateService?: string;
  oecId?: string;
}
